import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Tabs, Button, message, Spin, Alert } from 'antd';
import {
  FireOutlined,
  BulbOutlined,
  SoundOutlined,
  ThunderboltOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { environmentService, SystemOverview } from '../../services/environmentService';
import AirQualityMonitor from './components/AirQualityMonitor';
import LightingControl from './components/LightingControl';
import NoiseMonitoring from './components/NoiseMonitoring';
import EnergyManagement from './components/EnergyManagement';
import EnvironmentAlerts from './components/EnvironmentAlerts';
import SystemConfiguration from './components/SystemConfiguration';
import './EnvironmentControl.css';

const { TabPane } = Tabs;

interface EnvironmentStats {
  airQuality: {
    totalSensors: number;
    activeSensors: number;
    averageAQI: number;
    alertCount: number;
  };
  lighting: {
    totalSystems: number;
    activeSystems: number;
    energyConsumption: number;
    energySavings: number;
  };
  noise: {
    totalSensors: number;
    activeSensors: number;
    averageLevel: number;
    alertCount: number;
  };
  energy: {
    totalConsumption: number;
    dailyConsumption: number;
    efficiency: number;
    cost: number;
  };
}

const EnvironmentControl: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<EnvironmentStats | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  // 載入統計數據
  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await environmentService.getSystemOverview();
      setStats(data);
    } catch (error) {
      console.error('載入環境統計數據失敗:', error);
      message.error('載入環境統計數據失敗');
    } finally {
      setLoading(false);
    }
  };

  // 刷新數據
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadStats();
      message.success('數據刷新成功');
    } catch (error) {
      message.error('數據刷新失敗');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadStats();
    
    // 設置定時刷新
    const interval = setInterval(loadStats, 30000); // 每30秒刷新一次
    
    return () => clearInterval(interval);
  }, []);

  // 獲取空氣品質等級顏色
  const getAQIColor = (aqi: number) => {
    if (aqi <= 50) return '#52c41a'; // 優秀 - 綠色
    if (aqi <= 100) return '#faad14'; // 良好 - 黃色
    if (aqi <= 150) return '#fa8c16'; // 中等 - 橙色
    if (aqi <= 200) return '#f5222d'; // 差 - 紅色
    return '#722ed1'; // 危險 - 紫色
  };

  // 獲取能效等級顏色
  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency >= 90) return '#52c41a'; // 優秀
    if (efficiency >= 80) return '#faad14'; // 良好
    if (efficiency >= 70) return '#fa8c16'; // 中等
    return '#f5222d'; // 需改善
  };

  if (loading) {
    return (
      <div className="environment-control-loading">
        <Spin size="large" />
        <p>載入環境控制系統...</p>
      </div>
    );
  }

  return (
    <div className="environment-control">
      <div className="environment-control-header">
        <div className="header-content">
          <h1>環境控制系統</h1>
          <p>智能環境監控與控制管理平台</p>
        </div>
        <div className="header-actions">
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={refreshing}
          >
            刷新數據
          </Button>
        </div>
      </div>

      {stats && (
        <div className="environment-overview">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card air-quality">
                <Statistic
                  title="空氣品質指數"
                  value={stats.airQuality.averageAQI}
                  precision={0}
                  valueStyle={{ color: getAQIColor(stats.airQuality.averageAQI) }}
                  prefix={<FireOutlined />}
                  suffix="AQI"
                />
                <div className="stat-details">
                  <span>活躍感測器: {stats.airQuality.activeSensors}/{stats.airQuality.totalSensors}</span>
                  {stats.airQuality.alertCount > 0 && (
                    <Alert
                      message={`${stats.airQuality.alertCount} 個警報`}
                      type="warning"
                      showIcon
                      style={{ fontSize: '12px' }}
                    />
                  )}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card lighting">
                <Statistic
                  title="照明系統"
                  value={stats.lighting.activeSystems}
                  suffix={`/ ${stats.lighting.totalSystems}`}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<BulbOutlined />}
                />
                <div className="stat-details">
                  <span>能耗: {stats.lighting.energyConsumption.toFixed(1)} kWh</span>
                  <span className="energy-savings">
                    節能: {stats.lighting.energySavings.toFixed(1)}%
                  </span>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card noise">
                <Statistic
                  title="噪音監測"
                  value={stats.noise.averageLevel}
                  precision={1}
                  valueStyle={{ 
                    color: stats.noise.averageLevel > 70 ? '#f5222d' : '#52c41a' 
                  }}
                  prefix={<SoundOutlined />}
                  suffix="dB"
                />
                <div className="stat-details">
                  <span>監測點: {stats.noise.activeSensors}/{stats.noise.totalSensors}</span>
                  {stats.noise.alertCount > 0 && (
                    <Alert
                      message={`${stats.noise.alertCount} 個警報`}
                      type="error"
                      showIcon
                      style={{ fontSize: '12px' }}
                    />
                  )}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card energy">
                <Statistic
                  title="能源效率"
                  value={stats.energy.efficiency}
                  precision={1}
                  valueStyle={{ color: getEfficiencyColor(stats.energy.efficiency) }}
                  prefix={<ThunderboltOutlined />}
                  suffix="%"
                />
                <div className="stat-details">
                  <span>日耗電: {stats.energy.dailyConsumption.toFixed(1)} kWh</span>
                  <span>成本: NT$ {stats.energy.cost.toFixed(0)}</span>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      )}

      <div className="environment-content">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
          size="large"
        >
          <TabPane 
            tab={
              <span>
                <ExclamationCircleOutlined />
                即時警報
              </span>
            } 
            key="alerts"
          >
            <EnvironmentAlerts onRefresh={handleRefresh} />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <FireOutlined />
                空氣品質
              </span>
            } 
            key="air-quality"
          >
            <AirQualityMonitor onRefresh={handleRefresh} />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <BulbOutlined />
                照明控制
              </span>
            } 
            key="lighting"
          >
            <LightingControl onRefresh={handleRefresh} />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <SoundOutlined />
                噪音監測
              </span>
            } 
            key="noise"
          >
            <NoiseMonitoring onRefresh={handleRefresh} />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <ThunderboltOutlined />
                能源管理
              </span>
            } 
            key="energy"
          >
            <EnergyManagement onRefresh={handleRefresh} />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                系統配置
              </span>
            } 
            key="config"
          >
            <SystemConfiguration onRefresh={handleRefresh} />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default EnvironmentControl;